import { useEffect, useRef } from "react"
import { useRouter } from "next/router"

import { useQueryParams } from "@/hooks/useQueryParams"

const firstQueryValue = (
  value: string | string[] | undefined,
): string | undefined => (Array.isArray(value) ? value[0] : value)

/**
 * Reports arrivals at `/uploaded?upload=true`, the href behind the sidebar's
 * upload shortcut, and drops the parameter so a reload does not reopen the
 * dialog. It reports rather than latches: the second arrival has to reach an
 * upload control the reader already dismissed once. Each arrival reports once —
 * the URL replace lands a render or more later, and re-running until then would
 * report the same arrival again.
 */
export function useUploadDeepLink(onUploadRequested: () => void): void {
  const router = useRouter()
  const queryParams = useQueryParams()
  const hasReportedArrivalRef = useRef(false)

  useEffect(() => {
    if (!router.isReady) {
      return
    }
    if (firstQueryValue(router.query.upload) !== "true") {
      hasReportedArrivalRef.current = false
      return
    }
    if (hasReportedArrivalRef.current) {
      return
    }
    hasReportedArrivalRef.current = true
    onUploadRequested()
    queryParams.setMultiple(
      { upload: null },
      { history: "replace", shallow: true },
    )
  }, [onUploadRequested, queryParams, router.isReady, router.query.upload])
}
